/**
 * 공통 HTTP 계층 — 백엔드 /api/admin/* 호출 래퍼의 기반.
 *
 * - dev: vite proxy가 /api → http://localhost:3000 으로 포워딩
 * - prod: nginx 가 동일 origin 으로 처리
 *
 * 사용자가 다른 origin으로 호출하고 싶다면 VITE_API_BASE 환경변수 설정.
 *
 * 인증 (P6 — Admin Login):
 *   - lib/auth 의 getAccessToken() 으로 매 요청마다 동적 토큰 첨부
 *   - 401 응답 시 자동 clearAuth + LoginView redirect
 *
 * 도메인별 API 함수는 *-api.ts 파일들에 분산 — 이 파일의 헬퍼를 import 해서 사용.
 * (호출부는 기존처럼 api/client.ts 배럴을 통해 접근)
 */

import { getAccessToken, clearAuth } from '../lib/auth';

export const API_BASE: string = import.meta.env.VITE_API_BASE ?? '';

/**
 * 공통 헤더 — 모든 호출이 이 헬퍼 사용.
 *
 * lib/auth 의 토큰을 매 요청마다 가져옴 (동적).
 * 토큰 없으면 Authorization 헤더 미첨부 (login/refresh 같은 비인증 요청).
 */
export function authHeaders(extra: Record<string, string> = {}): Record<string, string> {
  const h: Record<string, string> = { Accept: 'application/json', ...extra };
  const token = getAccessToken();
  if (token) {
    h.Authorization = `Bearer ${token}`;
  }
  return h;
}

/**
 * 401 응답 처리 — 토큰 만료/위조 등.
 *
 * 동작:
 *   1. clearAuth() — localStorage 정리
 *   2. 현재 URL 을 sessionStorage 에 임시 저장 (로그인 후 복귀)
 *   3. /login 으로 redirect (페이지 reload — 라우터 가드 작동)
 */
export function handleUnauthorized(): void {
  clearAuth();
  if (location.pathname !== '/login' && location.pathname !== '/change-password') {
    sessionStorage.setItem('hcltech_post_login_redirect', location.pathname + location.search);
    location.replace('/login');
  }
}

// ─── 전역 로딩 인디케이터 ─────────────────────────────────────────────
// 서버 요청(fetch) 대기 동안 화면 상단 중앙에 "처리 중…" pill 표시.
// 동시 요청은 카운터로 묶어 마지막 응답이 와야 사라짐. 클릭 막지 않음(pointer-events:none).
// 스타일은 최초 1회 <style> 주입 — 특정 CSS 파일 로드 여부와 무관.
let _inflight = 0;
let _showTimer: number | undefined;
let _hideTimer: number | undefined;
const _SHOW_DELAY = 140;   // 이보다 빨리 끝나는 요청은 아예 안 띄움 (반짝임 방지)
const _HIDE_GRACE = 300;   // 인플라이트 0 이 돼도 잠깐 유지 — 직후 후속 요청이 오면 그대로 (깜빡임 방지)
function _ensureLoadingEl(): HTMLElement | null {
  if (typeof document === 'undefined') return null;
  let bar = document.getElementById('global-loading-bar');
  if (!bar) {
    const style = document.createElement('style');
    style.textContent =
      '#global-loading-bar{position:fixed;top:14px;left:50%;transform:translateX(-50%) translateY(-8px);' +
      'z-index:99999;display:flex;align-items:center;gap:8px;padding:6px 14px;background:rgba(17,24,39,.86);' +
      'color:#fff;font-size:12px;font-weight:600;border-radius:999px;opacity:0;pointer-events:none;' +
      'box-shadow:0 4px 14px rgba(0,0,0,.18);transition:opacity .18s,transform .18s}' +
      '#global-loading-bar.is-active{opacity:1;transform:translateX(-50%) translateY(0)}' +
      '#global-loading-bar::before{content:"";width:13px;height:13px;border:2px solid rgba(255,255,255,.35);' +
      'border-top-color:#fff;border-radius:50%;animation:global-loading-spin .7s linear infinite}' +
      '@keyframes global-loading-spin{to{transform:rotate(360deg)}}';
    document.head.appendChild(style);
    bar = document.createElement('div');
    bar.id = 'global-loading-bar';
    bar.textContent = '처리 중…';
    document.body.appendChild(bar);
  }
  return bar;
}
function _isShown(): boolean {
  return document.getElementById('global-loading-bar')?.classList.contains('is-active') ?? false;
}
function _loadingStart(): void {
  _inflight++;
  // 후속 요청이 들어옴 → 예정돼 있던 숨김 취소 (버스트를 하나로 이어 깜빡임 방지).
  if (_hideTimer !== undefined) { clearTimeout(_hideTimer); _hideTimer = undefined; }
  // 아직 안 떠 있고 표시 타이머도 없으면 지연 표시 예약.
  if (!_isShown() && _showTimer === undefined) {
    _showTimer = window.setTimeout(() => {
      _showTimer = undefined;
      if (_inflight > 0) _ensureLoadingEl()?.classList.add('is-active');
    }, _SHOW_DELAY);
  }
}
function _loadingEnd(): void {
  _inflight = Math.max(0, _inflight - 1);
  if (_inflight > 0) return;
  // 모든 요청 종료. 아직 표시 전이면(빠른 요청) 표시 자체를 취소 — 반짝임 없음.
  if (_showTimer !== undefined) { clearTimeout(_showTimer); _showTimer = undefined; }
  // 이미 떠 있으면 바로 끄지 말고 유예 — 그 사이 후속 요청이 오면 _loadingStart 가 취소.
  if (_isShown() && _hideTimer === undefined) {
    _hideTimer = window.setTimeout(() => {
      _hideTimer = undefined;
      if (_inflight === 0) document.getElementById('global-loading-bar')?.classList.remove('is-active');
    }, _HIDE_GRACE);
  }
}
/**
 * 네트워크 대기를 로딩 인디케이터로 감싼 fetch. 공통 JSON 헬퍼가 사용.
 * silent=true 면 글로벌 펄을 띄우지 않음 — 자체 로딩 표시가 있는 작은 요청용
 * (예: 상태 히스토리 팝오버는 안에 "불러오는 중…" 을 직접 그림).
 */
export async function loadingFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
  silent = false,
): Promise<Response> {
  if (silent) return fetch(input, init);
  _loadingStart();
  try {
    return await fetch(input, init);
  } finally {
    _loadingEnd();
  }
}

/**
 * 409 낙관적 잠금 충돌 (마이그 114 — 동시성_설계 §3-1).
 * 저장 핸들러가 instanceof 로 분기 — 자동 refetch + 드래프트 병합 UX.
 */
export class ApiConflictError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ApiConflictError';
  }
}

// ─── 응답 스키마 검증 (2026-07-12 — 응답 신뢰 → 검증 전환) ─────────────
//
// 요청은 서버 zValidator(shared zod)가 검증하지만, 응답은 지금까지 `as T` 단언
// (런타임 검증 없음)이었다. schema 를 주면 shared 의 zod 스키마로 응답을 parse —
// 서버·클라 타입이 배포 시점 차이 등으로 어긋나면 조용히 깨지는 대신 즉시 에러.
// 스키마 정본은 @hcltech/shared/schemas/* (UI 는 서브패스만 import — 배럴 금지).
import type { ZodType } from 'zod';

export function parseResponse<T>(data: unknown, schema: ZodType<T> | undefined, ctx: string): T {
  if (!schema) return data as T;   // 미지정 = 기존 동작 (점진 전환용)
  const r = schema.safeParse(data);
  if (!r.success) {
    // 드리프트 조기 발견이 목적 — 어느 필드가 어긋났는지 명시
    console.error(`[api] 응답 스키마 불일치 — ${ctx}`, r.error.issues);
    const top = r.error.issues.slice(0, 3)
      .map((i) => `${i.path.join('.') || '(root)'}: ${i.message}`).join(' · ');
    throw new Error(`응답 형식 불일치 (${ctx}) — ${top}`);
  }
  return r.data;
}

/**
 * 실패 응답 body → 사람이 읽을 수 있는 사유 한 줄.
 *
 * 백엔드(errorHandler.ts)는 항상 **객체**로 내려준다:
 *   { error: { code, message, details } }
 * 그런데 여러 api 파일이 `as { error?: string }` 로 단정하고 그대로
 * 템플릿에 넣어서 화면에 `[object Object]` 만 찍혔다. 진짜 사유가 그것에
 * 가려 무슨 일이 났는지 알 수 없었다(400 인데 왜 400 인지 모름).
 *
 * zValidator 기본 핸들러는 `{ success:false, error: ZodError }` 를 내므로
 * 그 경우도 `error.message`(문자열)를 집는다. 어느 쪽도 못 읽으면 빈 문자열.
 */
export function errorDetail(body: unknown): string {
  if (body == null || typeof body !== 'object') return '';
  const err = (body as { error?: unknown }).error;
  if (typeof err === 'string') return err;
  if (err != null && typeof err === 'object') {
    const msg = (err as { message?: unknown }).message;
    if (typeof msg === 'string') return msg;
  }
  const msg = (body as { message?: unknown }).message;
  return typeof msg === 'string' ? msg : '';
}

/** 실패 응답 → `HTTP <status> <사유>` Error. 수동 fetch 를 쓰는 곳의 공용 처리. */
export async function httpError(res: Response): Promise<Error> {
  const body = await res.json().catch(() => null);
  return new Error(`HTTP ${res.status} ${errorDetail(body)}`.trim());
}

export async function getJson<T>(
  path: string,
  opts: { signal?: AbortSignal; silent?: boolean; schema?: ZodType<T> } = {},
): Promise<T> {
  const res = await loadingFetch(`${API_BASE}${path}`, {
    headers: authHeaders(),
    signal: opts.signal,
  }, opts.silent);
  if (res.status === 401) {
    handleUnauthorized();
    throw new Error('인증이 만료되었습니다. 다시 로그인해주세요.');
  }
  if (!res.ok) {
    let detail = '';
    try {
      // backend errorHandler.ts 응답 형식: { error: { code, message, details } }
      const body = (await res.json()) as {
        error?: string | { message?: string };
      };
      if (typeof body.error === 'string') {
        detail = body.error;
      } else if (body.error?.message) {
        detail = body.error.message;
      }
    } catch {
      // ignore
    }
    throw new Error(`HTTP ${res.status} ${detail}`.trim());
  }
  return parseResponse(await res.json(), opts.schema, `GET ${path}`);
}

export async function postJson<T>(path: string, body: unknown, schema?: ZodType<T>): Promise<T> {
  const res = await loadingFetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body),
  });
  if (res.status === 401) {
    handleUnauthorized();
    throw new Error('인증이 만료되었습니다. 다시 로그인해주세요.');
  }
  if (!res.ok) {
    let detail = '';
    try {
      // backend errorHandler.ts 응답 형식: { error: { code, message, details } }
      const body = (await res.json()) as {
        error?: string | { message?: string };
        detail?: string;
      };
      if (typeof body.error === 'string') {
        detail = body.error;
      } else if (body.error?.message) {
        detail = body.error.message;
      } else if (body.detail) {
        detail = body.detail;
      }
    } catch {
      // ignore
    }
    throw new Error(`HTTP ${res.status} ${detail}`.trim());
  }
  return parseResponse(await res.json(), schema, `POST ${path}`);
}

// ─── PATCH helper (postJson 과 동일 구조) ─────────────────────────────
export async function patchJson<T>(path: string, body: unknown, schema?: ZodType<T>): Promise<T> {
  const res = await loadingFetch(`${API_BASE}${path}`, {
    method: 'PATCH',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body),
  });
  if (res.status === 401) {
    handleUnauthorized();
    throw new Error('인증이 만료되었습니다. 다시 로그인해주세요.');
  }
  if (!res.ok) {
    let detail = '';
    try {
      const errBody = (await res.json()) as {
        error?: string | { message?: string };
      };
      if (typeof errBody.error === 'string') detail = errBody.error;
      else if (errBody.error?.message) detail = errBody.error.message;
    } catch { /* ignore */ }
    throw new Error(`HTTP ${res.status} ${detail}`.trim());
  }
  return parseResponse(await res.json(), schema, `PATCH ${path}`);
}
